import { isAddress, zeroAddress } from "viem";
import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { sameAddress, shortAddr } from "../lib/address";
import { isOffered, noLongerOfferedLine } from "../lib/coins";
import type { Bound, DailyCapRow, MakerChain, PairBoundRow } from "../lib/api";
import { addressUrl, explorerName } from "../lib/explorer";
import { directionGerund, directionName, directionsText } from "../lib/pairs";
import { amountRefusal, formatToken, inputText, parseAmount } from "../lib/units";
import { useStateFrom } from "../lib/useStateFrom";
import type { OwnerReviewCallsProps, ReviewCall } from "./OwnerReviewCalls";
import type { OwnerReviewProps, ReviewChange } from "./OwnerReviewDialog";
import { Card, Pill, WhoTag, type Who } from "./ui";

/**
 * The executor's on-chain limits as table rows: the switches, a pair bound per
 * direction and a daily cap per token. Only the executor's owner wallet changes
 * them, through the owner review; for any other wallet each value is text.
 */

/** A change the page opens the owner review for; the page adds the network, its name and the signer role. */
export type Review = Omit<OwnerReviewProps, "onVerified" | "onClose" | "chain" | "page" | "signerRole">;

/** Several calls of the owner wallet in one review ("Set both price limits"). */
export type CallsReview = Omit<OwnerReviewCallsProps, "onVerified" | "onClose" | "chain" | "page" | "signerRole">;

/** The executor stores a daily cap in 192 bits. */
export const MAX_CAP = (1n << 192n) - 1n;

export const pctOfBps = (bps: number) => `${(bps / 100).toFixed(2)}%`;
export const hoursText = (sec: number) => {
  const h = sec / 3600;
  return Number.isInteger(h) ? String(h) : String(Number(h.toFixed(4)));
};
const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

/** Why typed bps are refused, or null. */
export function bpsRefusal(text: string, range?: PairBoundRow["deviation"]): string | null {
  if (!range) return "The allowed distance could not be read.";
  const t = text.replace(/[,\s]/g, "");
  if (t === "") return "Enter a value.";
  if (!/^\d+$/.test(t) || !Number.isSafeInteger(Number(t)) || Number(t) < range.min || Number(t) > range.max) return `A whole number from ${range.min.toLocaleString("en-US")} to ${range.max.toLocaleString("en-US")} bps.`;
  return null;
}

/** Typed hours in seconds, or why they are refused. */
export function hoursToSeconds(text: string, range?: PairBoundRow["staleness"]): { sec: number } | { refusal: string } {
  if (!range) return { refusal: "The allowed feed age could not be read." };
  const t = text.replace(/[,\s]/g, "");
  if (t === "") return { refusal: "Enter a value." };
  if (!/^\d+(\.\d+)?$/.test(t)) return { refusal: "Hours, such as 6 or 0.5." };
  const sec = Math.round(Number(t) * 3600);
  if (!Number.isSafeInteger(sec) || sec < range.minSec || sec > range.maxSec) return { refusal: `From ${range.minSec} seconds to ${range.maxSec / 3600} h.` };
  return { sec };
}

/** Before the executor is deployed on a network: an inset box inside the card, nothing to set yet, and who acts next. */
export function NotDeployed({ chain, others, owner, what }: { chain: MakerChain; others: string; owner: string | null; what: string }) {
  return (
    <div className="mk-inset">
      <div className="mk-inset-head">
        <h3 className="mk-inset-title">Not deployed on {chain.label} yet</h3>
        <Pill tone="grey">Waiting for AileFX</Pill>
      </div>
      <p className="mk-text mk-inset-text">
        AileFX deploys your executor on {chain.label}
        {others !== "" ? `, at the same address as on ${others}` : ""}. Then {owner !== null ? "the owner wallet " : "your owner wallet "}
        {owner !== null && <span className="mk-mono mk-strong">{shortAddr(owner)}</span>}
        {owner !== null ? " " : ""}sets {what} here. Every step and who takes it: <Link to="/executor">Executor</Link>.
      </p>
    </div>
  );
}

export function LimitsCard({ title, heads, who = "owner", id, units = "Units", children }: { title: string; heads: [string, string, string]; who?: Who; id?: string; units?: string; children: ReactNode }) {
  return (
    <Card title={title} tag={<WhoTag who={who} />} {...(id === undefined ? {} : { id })}>
      <table className="mk-table lim-table">
        <thead>
          <tr>
            <th scope="col" style={{ width: 280 }}>
              {heads[0]}
            </th>
            <th scope="col" style={{ width: 250 }}>
              {heads[1]}
            </th>
            <th scope="col" style={{ width: 250 }}>
              {heads[2]}
            </th>
            <th scope="col" style={{ width: 110 }}>
              {units}
            </th>
            <th scope="col">
              <span className="sr-only">Set</span>
            </th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Card>
  );
}

export function EmptyRow({ text }: { text: string }) {
  return (
    <tr>
      <td colSpan={5} className="mk-text">
        {text}
      </td>
    </tr>
  );
}

export function RowName({ name, sub }: { name: string; sub: ReactNode }) {
  return (
    <th scope="row">
      <div className="mk-row-name">
        <span className="mk-row-name-text">{name}</span>
        <span className="mk-sub">{sub}</span>
      </div>
    </th>
  );
}

export function SetCell({ show, label, onClick }: { show: boolean; label: string; onClick: () => void }) {
  return (
    <td>
      <div className="lim-set">
        {show && (
          <button type="button" className="mk-btn mk-btn-set" aria-label={label} onClick={onClick}>
            Set
          </button>
        )}
      </div>
    </td>
  );
}

export function SwitchRow({
  name,
  sub,
  current,
  unknown = false,
  label,
  desc,
  canEdit,
  onSet,
}: {
  name: string;
  sub: string;
  current: boolean;
  unknown?: boolean;
  label: string;
  desc: string;
  canEdit: boolean;
  onSet: (next: boolean) => void;
}) {
  const id = useId();
  const currentText = current ? "on" : "off";
  const [text, setText] = useStateFrom(currentText);
  const changed = text !== currentText || unknown;
  return (
    <tr>
      <RowName name={name} sub={sub} />
      <td>
        <div className="mk-cell">
          {canEdit ? (
            <>
              <label htmlFor={id} className="sr-only">
                {label}
              </label>
              <select id={id} className="mk-select" value={text} onChange={(e) => setText(e.target.value)}>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </>
          ) : (
            <span className="mk-value">{unknown ? "—" : current ? "On" : "Off"}</span>
          )}
        </div>
      </td>
      <td>
        <span className="mk-value mk-value-plain">{desc}</span>
      </td>
      <td>
        <div className="mk-units">—</div>
      </td>
      <SetCell show={canEdit && changed} label={`Set ${label}`} onClick={() => onSet(text === "on")} />
    </tr>
  );
}

/**
 * A pair bound per direction of a pair AileFX quotes. `feedName` is the feed's
 * own description ("CAD / USD") when the backend names it, else the feed is
 * named by its address; `both` names both coins in the direction, where the
 * network has more than one pair.
 */
export function BoundRow({
  chain,
  row,
  executor,
  canEdit,
  onReview,
  feedName = null,
  both = false,
}: {
  chain: MakerChain;
  row: PairBoundRow;
  executor: string;
  canEdit: boolean;
  onReview: (r: Review) => void;
  feedName?: string | null;
  both?: boolean;
}) {
  const ids = useId();
  const base = row.bound ?? (row.suggestedFrom === "pair" && row.suggested !== null ? { ...row.suggested, maxDeviationBps: null, maxStalenessSec: null } : null);
  const startBps = base?.maxDeviationBps == null ? "" : base.maxDeviationBps.toLocaleString("en-US");
  const startH = base?.maxStalenessSec == null ? "" : hoursText(base.maxStalenessSec);
  const key = `${startBps}|${startH}|${row.bound === null ? "none" : "set"}`;
  const [bpsText, setBps] = useStateFrom(startBps, key);
  const [hText, setH] = useStateFrom(startH, key);

  const name = directionName(row.pair, row.side, both);
  const gerund = directionGerund(row.pair, row.side, both);
  const [manualFeed, setManualFeed] = useStateFrom("", key);
  const [reading, setReading] = useStateFrom("", key);
  const feed = base?.feed ?? (isAddress(manualFeed) && manualFeed.toLowerCase() !== zeroAddress ? manualFeed : null);
  const invert = base?.invert ?? (reading === "direct" ? false : reading === "inverse" ? true : null);
  const feedText = (address: string) => (feedName !== null && sameAddress(address, feed) ? feedName : shortAddr(address));
  const subName = feed === null ? `You pay ${row.inSymbol}` : feedName !== null ? `You pay ${row.inSymbol} · ${feedName} feed` : `You pay ${row.inSymbol} · feed ${shortAddr(feed)}`;
  // A value in force is never refused as it stands; a typed or prefilled one is.
  const bpsBad = canEdit && (row.bound === null || bpsText !== startBps) ? bpsRefusal(bpsText, row.deviation) : null;
  const age = hoursToSeconds(hText, row.staleness);
  const ageBad = canEdit && (row.bound === null || hText !== startH) && "refusal" in age ? age.refusal : null;
  const nextBps = bpsRefusal(bpsText, row.deviation) === null ? Number(bpsText.replace(/[,\s]/g, "")) : null;
  const nextSec = "sec" in age ? age.sec : null;
  const changed =
    row.bound === null ? true : nextBps !== row.bound.maxDeviationBps || nextSec !== row.bound.maxStalenessSec;
  const canSet = canEdit && feed !== null && invert !== null && bpsRefusal(bpsText, row.deviation) === null && "sec" in age && changed && nextBps !== null && nextSec !== null;

  const note = row.bound === null ? { text: "Not set: nobody can fill this direction. Choose a distance and feed age for your pair.", warn: true } : null;

  const set = () => {
    if (!canSet || feed === null || invert === null || nextBps === null || nextSec === null) return;
    onReview({
      title: `Set the pair bound for ${gerund} on ${chain.label}`,
      lead: `In this direction your executor pays ${row.inSymbol} and receives ${row.outSymbol}. It will refuse any fill that gives it more than ${pctOfBps(nextBps)} less ${row.outSymbol} than the ${feedName === null ? "feed" : `${feedName} feed`} implies, and any fill while the feed is older than ${hoursText(nextSec)} h.`,
      request: {
        kind: "pairBound",
        params: { tokenIn: row.tokenIn, tokenOut: row.tokenOut, feed: feed, invert: invert, maxDeviationBps: nextBps, maxStalenessSec: nextSec },
      },
      changes: [
        { label: "Max distance from the feed", from: row.bound === null ? "Not set" : `${row.bound.maxDeviationBps.toLocaleString("en-US")} bps (${pctOfBps(row.bound.maxDeviationBps)})`, to: `${nextBps.toLocaleString("en-US")} bps (${pctOfBps(nextBps)})` },
        { label: "Feed max age", from: row.bound === null ? "Not set" : `${hoursText(row.bound.maxStalenessSec)} h`, to: `${hoursText(nextSec)} h` },
        { label: "Feed", from: row.bound === null ? "Not set" : feedText(row.bound.feed), to: feedText(feed) },
      ],
      callText: `setPairBound(${shortAddr(row.tokenIn)} ${row.inSymbol}, ${shortAddr(row.tokenOut)} ${row.outSymbol}, ${shortAddr(feed)} ${feedName !== null && sameAddress(feed, feed) ? `${feedName} feed` : "feed"}, ${String(invert)}, ${nextBps}, ${nextSec})`,
      callSub: `${String(invert)} = ${invert ? `the feed read the other way round, from ${row.inSymbol} to ${row.outSymbol}` : "the feed read as published"}; ${nextBps} bps = ${pctOfBps(nextBps)}; ${nextSec} s = ${hoursText(nextSec)} h, as in the calldata`,
      contract: "Your executor",
      expectedTo: executor,
      done: `Verified on chain: ${gerund} on ${chain.label} now have a pair bound of ${nextBps.toLocaleString("en-US")} bps and ${hoursText(nextSec)} h.`,
    });
  };

  return (
    <tr>
      <RowName name={name} sub={<>{subName}{canEdit && base === null && <span className="native-setting-field"><label>Feed address<input className="mk-input" aria-label={`Feed address when ${lowerFirst(name)}`} placeholder="Not set" value={manualFeed} aria-invalid={manualFeed !== "" && feed === null} onChange={e => setManualFeed(e.target.value)} /></label>{manualFeed !== "" && feed === null && <span className="mk-error">Enter a valid nonzero feed address.</span>}<label>Reading<select className="mk-input" aria-label={`Feed reading when ${lowerFirst(name)}`} value={reading} onChange={e => setReading(e.target.value)}><option value="">Not set</option><option value="direct">As published</option><option value="inverse">The other way round</option></select></label></span>}</>} />
      <td>
        <div className="mk-cell">
          {canEdit ? (
            <>
              <label htmlFor={`${ids}-d`} className="sr-only">
                Max distance from the feed when {lowerFirst(name)}
              </label>
              <input
                id={`${ids}-d`}
                className="mk-input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={bpsText}
                aria-invalid={bpsBad !== null}
                onChange={(e) => setBps(e.target.value)}
              />
              {bpsBad !== null && (
                <span className="mk-error" role="alert">
                  {bpsBad}
                </span>
              )}
            </>
          ) : (
            <span className="mk-value">{row.bound === null ? "Not set" : row.bound.maxDeviationBps.toLocaleString("en-US")}</span>
          )}
          {row.deviation !== undefined && <span className="mk-sub">From {row.deviation.min} to {row.deviation.max.toLocaleString("en-US")} bp.</span>}
          {note !== null && <span className={note.warn ? "mk-sub mk-sub-warn lim-note" : "mk-sub lim-note"}>{note.text}</span>}
        </div>
      </td>
      <td>
        <div className="mk-cell">
          {canEdit ? (
            <>
              <label htmlFor={`${ids}-a`} className="sr-only">
                Feed max age when {lowerFirst(name)}
              </label>
              <input
                id={`${ids}-a`}
                className="mk-input"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={hText}
                aria-invalid={ageBad !== null}
                onChange={(e) => setH(e.target.value)}
              />
              {ageBad !== null && (
                <span className="mk-error" role="alert">
                  {ageBad}
                </span>
              )}
            </>
          ) : (
            <span className="mk-value">{row.bound === null ? "—" : hoursText(row.bound.maxStalenessSec)}</span>
          )}
          {row.staleness !== undefined && <span className="mk-sub">Allowed feed age: {row.staleness.minSec} s–{hoursText(row.staleness.maxSec)} h.</span>}
        </div>
      </td>
      <td>
        <div className="mk-units">bps · h</div>
      </td>
      <SetCell show={canSet} label={`Set the pair bound for ${gerund} on ${chain.label}`} onClick={set} />
    </tr>
  );
}

/** A pair's heading row inside the pair bounds table: its name, a line under it, and an action. */
export function PairHeadRow({ id, pair, sub, action }: { id?: string; pair: string; sub: ReactNode; action?: ReactNode }) {
  return (
    <tr className="lim-group" {...(id === undefined ? {} : { id })}>
      <td colSpan={5}>
        <div className="lim-group-line">
          <span className="lim-group-text">
            <span className="lim-group-name">{pair}</span>
            <span className="mk-sub">{sub}</span>
          </span>
          {action}
        </div>
      </td>
    </tr>
  );
}

/** The feed both price limits of an own pair read: its feed as the book saved it, which each direction's suggestion names. */
export function pairFeedOf(rows: readonly PairBoundRow[]): string | null {
  return rows.find((r) => r.suggested !== null)?.suggested?.feed ?? null;
}

/** Whether a direction's bound in force reads its own pair's feed with the derived reading. */
function followsPair(row: PairBoundRow): boolean {
  if (row.matchesPair !== null) return row.matchesPair;
  return row.bound !== null && row.suggested !== null && sameAddress(row.bound.feed, row.suggested.feed) && row.bound.invert === row.suggested.invert;
}

/** "BRL / USD read as BRZ per 1 USDC, 500 bps (5.00%), 48 h": a price limit in the review's words. */
function limitWords(row: PairBoundRow, bound: Bound, feedName: string | null, pairFeed: string | null): string {
  const tail = `${bound.maxDeviationBps.toLocaleString("en-US")} bps (${pctOfBps(bound.maxDeviationBps)}), ${hoursText(bound.maxStalenessSec)} h`;
  if (pairFeed === null || !sameAddress(bound.feed, pairFeed)) return `Another feed ${shortAddr(bound.feed)}, ${bound.maxDeviationBps.toLocaleString("en-US")} bps, ${hoursText(bound.maxStalenessSec)} h`;
  const feed = feedName ?? `The feed ${shortAddr(bound.feed)}`;
  return row.suggested !== null && bound.invert !== row.suggested.invert ? `${feed} read the other way round, ${tail}` : `${feed} read as ${row.outSymbol} per 1 ${row.inSymbol}, ${tail}`;
}

/**
 * A pair of the market maker's own: one row per direction, prefilled from the
 * pair’s feed and reading. Distance and age are elected by its owner. "Set both price limits" opens one review of
 * the two calls while a direction does not follow the pair. A bound that reads
 * another feed, or the feed the other way round, is flagged: the backend does
 * not quote that direction.
 */
export function OwnPairBounds({
  chain,
  pair,
  rows,
  feedName,
  executor,
  canEdit,
  gone,
  onReview,
  onReviewCalls,
}: {
  chain: MakerChain;
  pair: string;
  /** Its two directions. */
  rows: readonly PairBoundRow[];
  /** What the feed both limits read calls itself (never verified); null when it says nothing, or when the words are another feed's. */
  feedName: string | null;
  executor: string;
  canEdit: boolean;
  /** The network no longer offers the pair as it is saved: nothing to set, it is removed on Pairs. */
  gone: boolean;
  onReview: (r: Review) => void;
  onReviewCalls: (r: CallsReview) => void;
}) {
  const pairFeed = pairFeedOf(rows);
  // A direction that follows the pair starts from its bound in force; any other from the pair's suggestion.
  const start = (r: PairBoundRow) => {
    const b = followsPair(r) ? r.bound : (r.suggested === null ? r.bound : { ...r.suggested, maxDeviationBps: null, maxStalenessSec: null });
    return { bps: b?.maxDeviationBps == null ? "" : b.maxDeviationBps.toLocaleString("en-US"), h: b?.maxStalenessSec == null ? "" : hoursText(b.maxStalenessSec) };
  };
  const starts = rows.map(start);
  const key = JSON.stringify([starts, rows.map((r) => [r.bound, r.matchesPair])]);
  const [texts, setTexts] = useStateFrom(starts, key);

  const typed = starts.map((first, i) => {
    const t = texts[i] ?? first;
    const age = hoursToSeconds(t.h, rows[i]!.staleness);
    const bps = bpsRefusal(t.bps, rows[i]?.deviation) === null ? Number(t.bps.replace(/[,\s]/g, "")) : null;
    return { bps, sec: "sec" in age ? age.sec : null, bpsBad: bpsRefusal(t.bps, rows[i]?.deviation), ageBad: "refusal" in age ? age.refusal : null };
  });
  const allValid = typed.every((t) => t.bps !== null && t.sec !== null);
  const needsBoth = !gone && pairFeed !== null && rows.some((r) => !followsPair(r)) && rows.every((r) => r.suggested !== null);
  const canSetBoth = canEdit && needsBoth && allValid;

  const label = (i: number) => directionName(pair, rows[i]!.side, true);

  const setBoth = () => {
    if (!canSetBoth) return;
    const next: Bound[] = rows.map((r, i) => ({ feed: r.suggested!.feed, invert: r.suggested!.invert, maxDeviationBps: typed[i]!.bps!, maxStalenessSec: typed[i]!.sec! }));
    const same = next.every((b) => b.maxDeviationBps === next[0]!.maxDeviationBps && b.maxStalenessSec === next[0]!.maxStalenessSec);
    const changes: ReviewChange[] = rows.map((r, i) => ({
      label: label(i),
      from: r.bound === null ? "Not set" : limitWords(r, r.bound, feedName, pairFeed),
      to: limitWords(r, next[i]!, feedName, pairFeed),
    }));
    const calls: ReviewCall[] = rows.map((r, i) => {
      const b = next[i]!;
      const gerund = directionGerund(pair, r.side, true);
      const name = directionName(pair, r.side, true);
      return {
        head: name.charAt(0).toLowerCase() + name.slice(1),
        request: { kind: "pairBound", params: { tokenIn: r.tokenIn, tokenOut: r.tokenOut, feed: b.feed, invert: b.invert, maxDeviationBps: b.maxDeviationBps, maxStalenessSec: b.maxStalenessSec } },
        callText: `setPairBound(${shortAddr(r.tokenIn)} ${r.inSymbol}, ${shortAddr(r.tokenOut)} ${r.outSymbol}, ${shortAddr(b.feed)} ${feedName === null ? "feed" : `${feedName} feed`}, ${String(b.invert)}, ${b.maxDeviationBps}, ${b.maxStalenessSec})`,
        callSub: `${String(b.invert)} = ${b.invert ? "the feed read the other way round" : "the feed read as it answers"}, from ${r.inSymbol} to ${r.outSymbol}; ${b.maxDeviationBps} bps = ${pctOfBps(b.maxDeviationBps)}; ${b.maxStalenessSec} s = ${hoursText(b.maxStalenessSec)} h, as in the calldata`,
        done: `Verified on chain: ${gerund} on ${chain.label} now have a price limit of ${b.maxDeviationBps.toLocaleString("en-US")} bps and ${hoursText(b.maxStalenessSec)} h on ${feedName === null ? "your pair’s feed" : `the ${feedName} feed`}.`,
      };
    });
    onReviewCalls({
      title: `Set both price limits for ${pair} on ${chain.label}`,
      lead: same
        ? `Your pair ${pair} is priced from ${feedName === null ? "its feed" : `the ${feedName} feed`}, so both of its price limits read that feed. In each direction your executor will refuse any fill that gives it more than ${pctOfBps(next[0]!.maxDeviationBps)} less than the feed implies, and any fill while the feed is older than ${hoursText(next[0]!.maxStalenessSec)} h.`
        : `Your pair ${pair} is priced from ${feedName === null ? "its feed" : `the ${feedName} feed`}, so both of its price limits read that feed. In each direction your executor will refuse any fill that gives it less than the feed implies by more than that direction's distance below, and any fill while the feed is older than its age below.`,
      changes,
      calls,
      contract: "Your executor",
      expectedTo: executor,
    });
  };

  const link = pairFeed === null ? null : addressUrl(chain, pairFeed);
  const sub = gone ? (
    <>No longer offered on {chain.label} as it is saved: remove it on <Link to="/pairs">Pairs</Link>.</>
  ) : (
    <>
      Your pair. Both price limits read its feed{feedName === null ? "" : `, ${feedName}`}
      {pairFeed !== null && (
        <>
          {" "}
          {link === null ? (
            <span className="mk-mono">{shortAddr(pairFeed)}</span>
          ) : (
            <a href={link} target="_blank" rel="noreferrer noopener" className="mk-mono" aria-label={`The ${feedName ?? "price"} feed on ${explorerName(link)} (opens in a new tab)`}>
              {shortAddr(pairFeed)} ↗
            </a>
          )}
        </>
      )}
      , as your book does; only the distance and the age are yours to choose.
    </>
  );

  return (
    <>
      <PairHeadRow
        id={`ex-pair-${pair.replace("/", "-")}`}
        pair={pair}
        sub={sub}
        action={
          canSetBoth && (
            <button type="button" className="mk-btn mk-btn-set lim-both" aria-label={`Set both price limits for ${pair} on ${chain.label}`} onClick={setBoth}>
              Set both price limits
            </button>
          )
        }
      />
      {rows.map((r, i) => (
        <OwnBoundRow
          key={`${r.tokenIn}-${r.tokenOut}`}
          chain={chain}
          pair={pair}
          row={r}
          feedName={feedName}
          pairFeed={pairFeed}
          executor={executor}
          canEdit={canEdit && !gone}
          text={texts[i] ?? starts[i]!}
          start={starts[i]!}
          bpsBad={typed[i]!.bpsBad}
          ageBad={typed[i]!.ageBad}
          onText={(t) => setTexts((prev) => prev.map((x, j) => (j === i ? t : x)))}
          onReview={onReview}
        />
      ))}
    </>
  );
}

/** One direction of an own pair: its fields belong to the pair's group, so "Set both price limits" reads them. */
function OwnBoundRow({
  chain,
  pair,
  row,
  feedName,
  pairFeed,
  executor,
  canEdit,
  text,
  start,
  bpsBad,
  ageBad,
  onText,
  onReview,
}: {
  chain: MakerChain;
  pair: string;
  row: PairBoundRow;
  /** What the pair's feed calls itself, as the pair's group checked it; null when it says nothing. */
  feedName: string | null;
  pairFeed: string | null;
  executor: string;
  canEdit: boolean;
  text: { bps: string; h: string };
  start: { bps: string; h: string };
  bpsBad: string | null;
  ageBad: string | null;
  onText: (t: { bps: string; h: string }) => void;
  onReview: (r: Review) => void;
}) {
  const ids = useId();
  const name = directionName(pair, row.side, true);
  const gerund = directionGerund(pair, row.side, true);
  // The feed in words: what it calls itself, else its short address.
  const feedText = feedName ?? (pairFeed === null ? "its feed" : shortAddr(pairFeed));
  const follows = followsPair(row);
  const otherFeed = row.bound !== null && pairFeed !== null && !sameAddress(row.bound.feed, pairFeed);
  const otherReading = row.bound !== null && !otherFeed && row.suggested !== null && row.bound.invert !== row.suggested.invert;
  const typedBps = bpsBad === null ? Number(text.bps.replace(/[,\s]/g, "")) : null;
  const age = hoursToSeconds(text.h, row.staleness);
  const typedSec = "sec" in age ? age.sec : null;
  // A direction that follows the pair sets a new distance or age on its own; the feed and reading stay.
  const canSet =
    canEdit && follows && row.bound !== null && typedBps !== null && typedSec !== null && (typedBps !== row.bound.maxDeviationBps || typedSec !== row.bound.maxStalenessSec);
  const showBad = (bad: string | null, changed: boolean) => (canEdit && (changed || !follows) ? bad : null);

  let note: { text: string; tone: "warn" | "bad" | "plain" } | null;
  if (row.bound === null) note = { text: canEdit ? "Not set: nobody can fill this direction. Prefilled from your pair." : "Not set: nobody can fill this direction.", tone: "warn" };
  else if (otherFeed) note = { text: `Now reads another feed, ${shortAddr(row.bound.feed)}, so this direction is not quoted.${canEdit ? " Prefilled from your pair." : ""}`, tone: "bad" };
  else if (otherReading) note = { text: `Now reads your pair’s feed the other way round, so this direction is not quoted.${canEdit ? " Prefilled from your pair." : ""}`, tone: "bad" };
  else note = { text: "Follows your pair’s feed.", tone: "plain" };

  const set = () => {
    if (!canSet || row.bound === null || typedBps === null || typedSec === null) return;
    const b = row.bound;
    onReview({
      title: `Set the price limit for ${gerund} on ${chain.label}`,
      lead: `In this direction your executor pays ${row.inSymbol} and receives ${row.outSymbol}. It will refuse any fill that gives it more than ${pctOfBps(typedBps)} less ${row.outSymbol} than the ${feedText} feed implies, and any fill while the feed is older than ${hoursText(typedSec)} h.`,
      request: { kind: "pairBound", params: { tokenIn: row.tokenIn, tokenOut: row.tokenOut, feed: b.feed, invert: b.invert, maxDeviationBps: typedBps, maxStalenessSec: typedSec } },
      changes: [
        { label: "Max distance from the feed", from: `${b.maxDeviationBps.toLocaleString("en-US")} bps (${pctOfBps(b.maxDeviationBps)})`, to: `${typedBps.toLocaleString("en-US")} bps (${pctOfBps(typedBps)})` },
        { label: "Feed max age", from: `${hoursText(b.maxStalenessSec)} h`, to: `${hoursText(typedSec)} h` },
      ],
      callText: `setPairBound(${shortAddr(row.tokenIn)} ${row.inSymbol}, ${shortAddr(row.tokenOut)} ${row.outSymbol}, ${shortAddr(b.feed)} ${feedName === null ? "feed" : `${feedName} feed`}, ${String(b.invert)}, ${typedBps}, ${typedSec})`,
      callSub: `${String(b.invert)} = ${b.invert ? "the feed read the other way round" : "the feed read as it answers"}, from ${row.inSymbol} to ${row.outSymbol}; ${typedBps} bps = ${pctOfBps(typedBps)}; ${typedSec} s = ${hoursText(typedSec)} h, as in the calldata`,
      contract: "Your executor",
      expectedTo: executor,
      done: `Verified on chain: ${gerund} on ${chain.label} now have a price limit of ${typedBps.toLocaleString("en-US")} bps and ${hoursText(typedSec)} h on the ${feedText} feed.`,
    });
  };

  const shownBps = showBad(bpsBad, text.bps !== start.bps);
  const shownAge = showBad(ageBad, text.h !== start.h);
  return (
    <tr>
      <th scope="row">
        <div className="mk-row-name">
          <span className="mk-row-name-text">{name}</span>
          <span className="mk-sub">
            You pay {row.inSymbol} · {feedText}, read as {row.outSymbol} per 1 {row.inSymbol}
          </span>
          {otherFeed && (
            <span className="lim-flag">
              <Pill tone="bad">Another feed</Pill>
            </span>
          )}
          {otherReading && (
            <span className="lim-flag">
              <Pill tone="bad">Read the other way</Pill>
            </span>
          )}
        </div>
      </th>
      <td>
        <div className="mk-cell">
          {canEdit ? (
            <>
              <label htmlFor={`${ids}-d`} className="sr-only">
                Max distance from the feed when {name.charAt(0).toLowerCase() + name.slice(1)}
              </label>
              <input
                id={`${ids}-d`}
                className="mk-input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={text.bps}
                aria-invalid={shownBps !== null}
                onChange={(e) => onText({ ...text, bps: e.target.value })}
              />
              {shownBps !== null && (
                <span className="mk-error" role="alert">
                  {shownBps}
                </span>
              )}
            </>
          ) : (
            <span className="mk-value">{row.bound === null ? "Not set" : row.bound.maxDeviationBps.toLocaleString("en-US")}</span>
          )}
          {note !== null && <span className={`mk-sub lim-note${note.tone === "warn" ? " mk-sub-warn" : note.tone === "bad" ? " mk-sub-bad" : ""}`}>{note.text}</span>}
        </div>
      </td>
      <td>
        <div className="mk-cell">
          {canEdit ? (
            <>
              <label htmlFor={`${ids}-a`} className="sr-only">
                Feed max age when {name.charAt(0).toLowerCase() + name.slice(1)}
              </label>
              <input
                id={`${ids}-a`}
                className="mk-input"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={text.h}
                aria-invalid={shownAge !== null}
                onChange={(e) => onText({ ...text, h: e.target.value })}
              />
              {shownAge !== null && (
                <span className="mk-error" role="alert">
                  {shownAge}
                </span>
              )}
            </>
          ) : (
            <span className="mk-value">{row.bound === null ? "—" : hoursText(row.bound.maxStalenessSec)}</span>
          )}
          {row.staleness !== undefined && <span className="mk-sub">Allowed feed age: {row.staleness.minSec} s–{hoursText(row.staleness.maxSec)} h.</span>}
        </div>
      </td>
      <td>
        <div className="mk-units">bps · h</div>
      </td>
      <SetCell show={canSet} label={`Set the price limit for ${gerund} on ${chain.label}`} onClick={set} />
    </tr>
  );
}

/**
 * "Paid when customers buy CADC": the directions in which the executor pays
 * this coin, naming both coins where the network has more than one pair
 * ("Paid when customers buy BRZ with USDT or with USDC").
 */
export function paidWhen(token: string, pairBounds: readonly PairBoundRow[]): string {
  const both = new Set(pairBounds.map((p) => p.pair)).size > 1;
  const ways = pairBounds.filter((p) => sameAddress(p.tokenIn, token));
  return ways.length === 0 ? "Not in any of your pairs" : `Paid when ${directionsText(ways, both)}`;
}

/** "customers selling BRZ for USDC": the directions in which the executor pays this coin, for a sentence. */
function payingWhen(token: string, pairBounds: readonly PairBoundRow[]): string | null {
  const both = new Set(pairBounds.map((p) => p.pair)).size > 1;
  const ways = pairBounds.filter((p) => sameAddress(p.tokenIn, token));
  return ways.length === 0 ? null : directionsText(ways, both, "gerund");
}

export function CapRow({
  chain,
  row,
  pairBounds,
  executor,
  canEdit,
  onReview,
}: {
  chain: MakerChain;
  row: DailyCapRow;
  pairBounds: readonly PairBoundRow[];
  executor: string;
  canEdit: boolean;
  onReview: (r: Review) => void;
}) {
  const id = useId();
  const current = inputText(row.cap, row.decimals);
  const [text, setText] = useStateFrom(current);
  const typed = parseAmount(text, row.decimals);
  const bad = canEdit ? (amountRefusal(text, row.decimals) ?? (typed !== null && typed > MAX_CAP ? "Too large for a daily cap." : null)) : null;
  const changed = typed !== null && typed.toString() !== row.cap;
  const canSet = canEdit && bad === null && changed && typed !== null;
  const capText = row.cap === "0" ? "Stop" : formatToken(row.cap, row.decimals, row.symbol);
  // A coin a pair pays with no cap yet: new to the executor, and who gets no quote meanwhile.
  const paying = payingWhen(row.token, pairBounds);
  const unset = row.cap === "0" && paying !== null;

  const set = () => {
    if (!canSet || typed === null) return;
    const next = formatToken(typed.toString(), row.decimals, row.symbol);
    onReview({
      title: `Set the ${row.symbol} daily cap on ${chain.label}`,
      lead: `Your executor will pay at most ${next} a day; what it pays refills the cap over 24 hours.`,
      request: { kind: "dailyCap", params: { token: row.token, cap: typed.toString() } },
      changes: [{ label: `${row.symbol} daily cap`, from: row.cap === "0" ? "Stop" : formatToken(row.cap, row.decimals, row.symbol), to: next }],
      callText: `setDailyCap(${shortAddr(row.token)} ${row.symbol}, ${typed.toString()})`,
      callSub: `${typed.toString()} = ${next} (${row.decimals} decimals), as in the calldata`,
      contract: "Your executor",
      expectedTo: executor,
      done: `Verified on chain: the ${row.symbol} daily cap on ${chain.label} is now ${next}.`,
    });
  };

  // Coin | Daily cap (the field with its unit, and what is in force once it is changed) | Left today | Set.
  return (
    <tr>
      <th scope="row">
        <div className="mk-row-name">
          <span className="mk-row-name-text">{row.symbol}</span>
          <span className="mk-sub">{paidWhen(row.token, pairBounds)}</span>
          {unset && (
            <span className="lim-flag">
              <Pill tone="grey">New to your executor</Pill>
            </span>
          )}
        </div>
      </th>
      <td>
        <div className="mk-cell">
          {canEdit ? (
            <>
              <div className="mk-input-line">
                <label htmlFor={id} className="sr-only">
                  {row.symbol} daily cap
                </label>
                <input id={id} className="mk-input" type="text" inputMode="decimal" autoComplete="off" value={text} aria-invalid={bad !== null} onChange={(e) => setText(e.target.value)} />
                <span className="mk-input-unit">{row.symbol}</span>
              </div>
              {bad !== null && (
                <span className="mk-error" role="alert">
                  {bad}
                </span>
              )}
              {bad === null && changed && !unset && <span className="mk-sub">Now {capText}</span>}
            </>
          ) : (
            <span className="mk-value">{capText}</span>
          )}
          {unset && (
            <span className="mk-sub mk-sub-warn lim-cap-note">
              {canEdit ? "Not set: your" : "Your"} executor pays no {row.symbol}, so {paying} get no quote from your book.
            </span>
          )}
          {/* A coin AileFX added and then removed: still capped, so its cap stays a field. */}
          {!isOffered(row) && <span className="mk-sub mk-sub-warn lim-cap-note">{noLongerOfferedLine(row.symbol)}</span>}
        </div>
      </td>
      <td>
        <span className="mk-value mk-value-plain">{row.cap === "0" ? "None" : formatToken(row.available, row.decimals, row.symbol)}</span>
      </td>
      <SetCell show={canSet} label={`Set the ${row.symbol} daily cap on ${chain.label}`} onClick={set} />
    </tr>
  );
}
